import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect, useRef, useState, type CSSProperties } from "react";
import { useApp } from "../app/AppContext";
import { AppProvider } from "../app/AppProvider";
import { signOut, useSession } from "../lib/auth-client";
import { AsideRail } from "./AsideRail";
import { InstallButton } from "./InstallButton";
import { OfflineBanner } from "./OfflineBanner";
import { QuickAdd } from "./QuickAdd";
import { SideRail } from "./SideRail";
import { TimerChip } from "./TimerChip";

const TABS = [
  { to: "/app/plan", label: "Plan" },
  { to: "/app/tasks", label: "Tasks" },
  { to: "/app/board", label: "Board" },
  { to: "/app/matrix", label: "Matrix" },
  { to: "/app/notes", label: "Notes" },
  { to: "/app/stats", label: "Stats" },
] as const;

// Right panel (Focus timer) width — draggable, clamped, persisted.
const ASIDE_DEFAULT = 330;
const ASIDE_MIN = 264;
const ASIDE_MAX = 480;
const ASIDE_KEY = "founderdeck.asideW";
const clampAside = (n: number) => Math.max(ASIDE_MIN, Math.min(ASIDE_MAX, n));

function ShellChrome() {
  const navigate = useNavigate();
  const { data } = useSession();
  const app = useApp();

  const [asideW, setAsideW] = useState(() => {
    const saved = Number(localStorage.getItem(ASIDE_KEY));
    return Number.isFinite(saved) && saved > 0 ? clampAside(saved) : ASIDE_DEFAULT;
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  useEffect(() => {
    localStorage.setItem(ASIDE_KEY, String(asideW));
  }, [asideW]);

  function onResizeDown(e: React.PointerEvent) {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: asideW };
    setDragging(true);
    // Suppress text selection across the app while dragging.
    document.body.style.userSelect = "none";
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onResizeMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    // The aside is on the right, so dragging left (negative dx) widens it.
    const dx = e.clientX - dragRef.current.startX;
    setAsideW(clampAside(dragRef.current.startW - dx));
  }
  function onResizeUp(e: React.PointerEvent) {
    if (!dragRef.current) return;
    dragRef.current = null;
    setDragging(false);
    document.body.style.userSelect = "";
    e.currentTarget.releasePointerCapture(e.pointerId);
  }
  // Double-click the handle to reset to the default width.
  const resetAside = () => setAsideW(ASIDE_DEFAULT);

  async function onSignOut() {
    await signOut();
    navigate({ to: "/login" });
  }

  return (
    <div className="shell">
      <OfflineBanner />
      <header className="top">
        <div className="logo">
          Founder<span>Deck</span>
        </div>
        <nav className="tabs">
          {TABS.map((t) => (
            <Link key={t.to} to={t.to} className="tab" activeProps={{ className: "tab on" }}>
              {t.label}
            </Link>
          ))}
        </nav>
        <div className="top-right">
          <button className="iconbtn" title="Morning plan" aria-label="Morning plan" onClick={() => app.openPlan()}>
            ☀
          </button>
          <button className="iconbtn" title="Shutdown ritual" aria-label="Shutdown ritual" onClick={() => app.openShutdown()}>
            🌙
          </button>
          <TimerChip />
          <InstallButton />
          <Link to="/app/settings" className="iconbtn" title="Settings & team" aria-label="Settings and team">
            ⚙
          </Link>
          <button className="iconbtn" title="Command palette (⌘K)" aria-label="Command palette" onClick={() => app.openPalette()}>
            ⌘K
          </button>
          <span className="who">{data?.user?.email}</span>
          <button className="iconbtn" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <div className="qwrap">
        <QuickAdd
          projects={app.projects}
          defaultProjectId={app.filter !== "all" ? app.filter : null}
          onAdd={(input) => app.createTask(input)}
        />
      </div>

      <div className="layout" style={{ "--aside-w": `${asideW}px` } as CSSProperties}>
        <SideRail />
        <main className="main">
          <Suspense fallback={<div className="empty">Loading…</div>}>
            <Outlet />
          </Suspense>
        </main>
        <div
          className={`aside-resizer${dragging ? " dragging" : ""}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize focus panel"
          title="Drag to resize · double-click to reset"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onDoubleClick={resetAside}
        />
        <AsideRail />
      </div>
    </div>
  );
}

export function AppShell() {
  return (
    <AppProvider>
      <ShellChrome />
    </AppProvider>
  );
}
